import { gql, useMutation } from '@apollo/client';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Code,
  FormControl,
} from '@apollo/orbit';
// TODO: Update to use Orbit's hook instead
// eslint-disable-next-line no-restricted-imports
import { useToast } from '@chakra-ui/react';
import { useFormik } from 'formik';
import _ from 'lodash';
import React, { useCallback, useMemo } from 'react';
import * as Yup from 'yup';

import { useSchemaParts } from 'src/app/graph/documentationPage/schemaPage/hooks/useSchemaParts';
import { typeMapWithoutInternal } from 'src/app/graph/explorerPage/helpers/graphqlHelpers';
import { useSchema } from 'src/app/graph/hooks/useSchema';
import { SANDBOX_EMPTY_SCHEMA } from 'src/app/graph/sandboxShared/defaultSandboxSchema';
import { CustomToast } from 'src/components/customToast/CustomToast';
import { MultiSelect } from 'src/components/multiSelect/MultiSelect';
import { TagList } from 'src/components/tagList/TagList';
import { useDebouncedEffect } from 'src/hooks/useDebouncedEffect';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { AUTOSAVE_TIMEOUT_MS } from '../../checksConfigPage/constants';
import { UseGraphSettingsButton } from '../../variantChecksConfigPage/common/UseGraphSettingsButton';
import { SharedLinterCheckConfigProps } from '../shared';

export const AllowedTagNamesFragment = gql`
  fragment AllowedTagNamesFragment on GraphLinterConfiguration {
    allowedTagNames
  }
`;

const validationSchema = Yup.object().shape({
  allowedTagNames: Yup.array()
    .of(Yup.string())
    .test(
      'unique',
      'Tag names must be unique',
      (list) => list.length === new Set(list).size,
    ),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const ApprovedTagsCard = ({
  graphRef,
  isVariantConfig,
  linterConfig,
}: SharedLinterCheckConfigProps) => {
  const toast = useToast();

  const [updateLinterConfiguration] = useMutation<
    GraphQLTypes.LinterConfigAllowedTagsMutation,
    GraphQLTypes.LinterConfigAllowedTagsMutationVariables
  >(gql`
    mutation LinterConfigAllowedTagsMutation(
      $graphId: ID!
      $changes: GraphLinterConfigurationChangesInput!
    ) {
      service(id: $graphId) {
        id
        updateLinterConfiguration(changes: $changes) {
          allowedTagNames
        }
      }
    }
  `);
  const { values, errors, dirty, setFieldValue, submitForm } =
    useFormik<FormValues>({
      enableReinitialize: true,
      initialValues: {
        allowedTagNames: linterConfig.allowedTagNames,
      },
      onSubmit: async (formValues) => {
        // compare with values from BE and remove ones not present in list
        const removals = linterConfig.allowedTagNames.filter(
          (tag) => formValues.allowedTagNames.indexOf(tag) === -1,
        );

        const additions = formValues.allowedTagNames.filter(
          (tag) => linterConfig.allowedTagNames.indexOf(tag) === -1,
        );

        await updateLinterConfiguration({
          variables: {
            graphId: graphRef.graphId,
            changes: {
              allowedTagNameAdditions: additions.length ? additions : null,
              allowedTagNameRemovals: removals.length ? removals : null,
            },
          },
          update: (cache) => {
            cache.evict({
              id: cache.identify({
                __typename: 'Service',
                id: graphRef.graphId,
              }),
              fieldName: 'linterConfiguration',
            });
          },
        });

        toast({
          duration: 3000,
          position: 'bottom-right',
          isClosable: true,
          render: ({ onClose }) => (
            <CustomToast
              onClose={onClose}
              heading="Approved @tag names changed"
              description={
                <p>
                  You've successfully updated the list of approved tag names.
                </p>
              }
            />
          ),
        });
      },
      validationSchema,
    });

  const submitIfDirty = useCallback(() => {
    if (dirty && _.isEmpty(errors)) {
      submitForm();
    }
  }, [dirty, errors, submitForm]);

  useDebouncedEffect(submitIfDirty, AUTOSAVE_TIMEOUT_MS, [values]);

  const { activeSchema, supergraphSchema } = useSchema();
  const isSupergraph = !!supergraphSchema;
  const graphqlSchema =
    supergraphSchema ?? activeSchema ?? SANDBOX_EMPTY_SCHEMA;

  const typeMap = useMemo(() => {
    return typeMapWithoutInternal(graphqlSchema, isSupergraph);
  }, [graphqlSchema, isSupergraph]);

  const { allTags: allTagsInSchema } = useSchemaParts({
    graphqlSchema,
    typeMap,
    isSupergraph,
  });

  const allTags = useMemo(() => {
    return new Set([...(allTagsInSchema ?? [])]);
  }, [allTagsInSchema]);

  const selectedTags = useMemo(() => {
    return new Set(values.allowedTagNames);
  }, [values.allowedTagNames]);

  const setSelectedTags = useCallback(
    (tags: Set<string>) => {
      setFieldValue('allowedTagNames', Array.from(tags));
    },
    [setFieldValue],
  );

  const onSelectedTagRemoved = useCallback(
    (tag: string) => {
      const newTags = new Set<string>(selectedTags);
      newTags.delete(tag);
      setSelectedTags(newTags);
    },
    [selectedTags, setSelectedTags],
  );

  return (
    <Card id="approved-tag-name">
      <CardHeader>
        <CardTitle>Approved @tag names</CardTitle>
        <CardDescription>
          This is a list of all the approved directive <Code>@tag</Code> names
          for your schemas. e.g. <Code>@tag(name: "internal")</Code>
        </CardDescription>
        {isVariantConfig && (
          <FormControl>
            <UseGraphSettingsButton
              label="Use Graph Settings"
              tooltipContent="At this time, variant level overrides for the linter configuration are not available. Please make changes at the graph level."
              isSelected={true}
              // NOTE: We are only including graph level configuration at this
              // time so we should keep this disabled and leave the variant version
              // of this component in a read only state
              disabled={true}
              onChange={_.noop}
              trackingEventCategory="Lint Checks"
            />
          </FormControl>
        )}
      </CardHeader>
      <CardBody>
        <MultiSelect
          disabled={isVariantConfig}
          className="w-120 max-w-full"
          valueTitle="Tags"
          allValues={Array.from(allTags).filter(
            (tag) => !selectedTags.has(tag),
          )}
          canSearch={false}
          canAddNewValues
          onValueAdded={(tag) =>
            setSelectedTags(new Set([...selectedTags, tag]))
          }
          triggerNode={
            <TagList
              tags={selectedTags}
              onTagRemoved={onSelectedTagRemoved}
              disabled={isVariantConfig}
            />
          }
          selectedValues={selectedTags}
          setSelectedValues={setSelectedTags}
        />
      </CardBody>
    </Card>
  );
};
